import React, {useState} from 'react'
import {useObvioAuth} from 'obvio/auth'
import Typography from '@material-ui/core/Typography'
import withStyles from '@material-ui/core/styles/withStyles'
import {colors} from 'lib/ui/theme'
import {obvioRoutes} from 'obvio/Routes'
import {useForm} from 'react-hook-form'
import Page, {
  Button,
  Description,
  Error,
  Link,
  TextField,
} from 'obvio/auth/Page'
import {useDeepLink} from 'DeepLinkProvider'
import {useQueryParams} from 'lib/url'

/**
 * Redirect URLs must be from one of these domains. Redirect URLs are meant
 * for internal services to authenticate as an existing obvio user.
 * Similar to how OAuth works.
 */
const ALLOWED_REDIRECT_DOMAINS = [
  'chat.obv.io',
  'eventgfx.com',
  'localhost:3000',
  'localhost',
]

export default function Login() {
  const {register, handleSubmit, errors} = useForm()
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const {login} = useObvioAuth()
  const {clearDeepLink, deepLinkLocation} = useDeepLink()

  const redirectUrl = useRedirectUrl()

  const submit = (data: {email: string; password: string}) => {
    setSubmitting(true)
    login({
      email: data.email,
      password: data.password,
    })
      .catch((e) => {
        setError(e.message)
        setSubmitting(false)
      })
      .then((res) => {
        if (!res) {
          return
        }

        const {user, token} = res
        if (!user) {
          return
        }

        // If we have a validated external redirect URL, redirect there with the
        // access token appended as a query parameter.
        if (redirectUrl) {
          const url = new URL(redirectUrl)
          url.searchParams.set('obvio_access_token', token)
          window.location.href = url.toString()
          return
        }

        // Only checking for a Deep Link location when the user is actually
        // submitting the login form. The auth-tokens (below) don't qualify for a
        // deep link redirect.
        const redirectLocation = deepLinkLocation()

        if (redirectLocation) {
          clearDeepLink()
          window.location.href = redirectLocation
        }
      })
  }

  return (
    <Page>
      <div>WELCOME</div>
      <Description>Log into your account</Description>
      <form onSubmit={handleSubmit(submit)}>
        <TextField
          label="Email"
          type="email"
          fullWidth
          variant="outlined"
          disabled={submitting}
          name="email"
          inputProps={{
            ref: register({
              required: 'Email is required',
            }),
            'aria-label': 'obvio account email',
          }}
          error={!!errors.email}
          helperText={errors.email && errors.email.message}
        />
        <TextField
          label="Password"
          type="password"
          fullWidth
          variant="outlined"
          disabled={submitting}
          name="password"
          inputProps={{
            ref: register({
              required: 'Password is required',
            }),
            'aria-label': 'obvio account password',
          }}
          error={!!errors.password}
          helperText={errors.password && errors.password.message}
        />
        <Error>{error}</Error>
        <Button
          variant="contained"
          fullWidth
          color="primary"
          disabled={submitting}
          aria-label="submit login"
          type="submit"
        >
          Login
        </Button>
      </form>
      <Link to={obvioRoutes.forgot_password} aria-label="forgot password">
        Forgot Password?
      </Link>
      <CreateAccountText>
        Don't have an account yet?{' '}
        <Link to={obvioRoutes.registration} aria-label="create account">
          Create one now
        </Link>
      </CreateAccountText>
    </Page>
  )
}

export const CreateAccountText = withStyles({
  root: {
    color: colors.text.muted,
  },
})(Typography)

function useRedirectUrl(): string | null {
  const {redirect_url: redirectUrl} = useQueryParams()

  if (!redirectUrl) {
    return null
  }

  try {
    const url = new URL(redirectUrl)
    if (ALLOWED_REDIRECT_DOMAINS.includes(url.hostname)) {
      return redirectUrl
    }
  } catch {
    // Invalid URL
  }

  return null
}
